import React, { useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, Typography } from "@payping/web-components";
import { isBeforeToday, SDKDateObject, toJalali } from "@payping/utils/dateUtils";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { SchedulingType } from "@/pages/Shop/Invoice/forms/type";
import { SwitchRow } from "@/core/component";

interface Props {
  setSentDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDueDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  prev: () => void;
  next: () => void;
  loading: boolean;
  form: FormInstance;
}

const SecondStepInvoice: React.FC<Props> = (props: Props) => {
  const { setSentDate, setIsSchedule, setDueDate, prev, next, loading, form } = props;
  const { t } = useAppTranslation();
  const [repeatDates, setRepeatDates] = useState<SDKDateObject[]>([]);

  const handleRepeatDates = (values: any) => {
    const { sendDate, interval, scheduleType, dueDateAfterHowManyDay, count } = values;
    const dates: any = [];

    for (let i = 0; i < count; i += 1) {
      switch (scheduleType) {
        case 0: // روز
          dates.push(toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "day"], method: "add" }] }).object);
          break;
        case 1: // هفته
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "week"], method: "add" }] }).object,
          );
          break;
        case 2: // ماه
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "month"], method: "add" }] }).object,
          );
          break;
        case 3: // سال
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "year"], method: "add" }] }).object,
          );
          break;
        default:
          return undefined;
      }
      // dates.push(sw(dates.length ? dates[dates.length - 1] : sendDate));
    }

    if (values.dueDateAfterHowManyDay) {
      setDueDate(
        toJalali({
          date: sendDate,
          manipulate: [{ args: [dueDateAfterHowManyDay, "day"], method: "add" }],
          toFormat: " jDD jMMMM jYYYY",
        }).string,
      );
    }

    setRepeatDates(dates);
  };

  const onFinish = () => {
    next();
  };
  return (
    <Form<SchedulingType>
      canDeactivate
      scrollToFirstError={{ behavior: "smooth" }}
      form={form}
      onFinish={onFinish}
      layout={"vertical"}
      initialValues={{
        scheduleType: 0,
        count: 2,
        dueDateAfterHowManyDay: 1,
        interval: 1,
      }}
      onValuesChange={(changedValues, values) => {
        if ("sendDate" in changedValues) {
          setSentDate(changedValues.sendDate?.locale("fa").format(" jDD jMMMM jYYYY"));
        }
        if ("dueDate" in changedValues) {
          setDueDate(changedValues.dueDate?.locale("fa").format(" jDD jMMMM jYYYY"));
        }
        if ("dueDateAfterHowManyDay" in changedValues) {
          setDueDate(changedValues.dueDateAfterHowManyDay);
        }
        if (
          "sendDate" in changedValues ||
          "interval" in changedValues ||
          "scheduleType" in changedValues ||
          "dueDateAfterHowManyDay" in changedValues ||
          "count" in changedValues
        ) {
          handleRepeatDates({
            sendDate: values.sendDate,
            interval: values.interval,
            count: values.count,
            scheduleType: values.scheduleType,
            dueDateAfterHowManyDay: values.dueDateAfterHowManyDay,
          });
        }
      }}
    >
      <Form.Item
        label={t("shop.createInvoice.secondStep.startDate")}
        rules={[{ required: true }]}
        name="sendDate"
        wrapperCol={{ md: 8 }}
      >
        <DatePicker className="tw-w-full" disabledDate={(date) => isBeforeToday(date)} skeleton={loading} />
      </Form.Item>
      <Form.Item valuePropName={"checked"} name={"isSchedule"}>
        <SwitchRow
          onChange={(checked) => {
            if (checked) setIsSchedule(true);
            else {
              setIsSchedule(false);
            }
          }}
          label={t("shop.createInvoice.secondStep.repeat")}
          loading={loading}
        />
      </Form.Item>
      <Form.Item noStyle dependencies={["isSchedule"]}>
        {({ getFieldValue }) => {
          const isRepeat = getFieldValue("isSchedule");
          return (
            isRepeat && (
              <>
                <Row gutter={[8, 8]}>
                  <Col xs={6}>
                    <Form.Item label={t("invoice.create.base.repeatIn")} rules={[{ required: true }]}>
                      <Space.Compact>
                        <Form.Item name="interval" noStyle>
                          <Input onlyNumbers skeleton={loading} />
                        </Form.Item>
                        <Form.Item name="scheduleType" noStyle>
                          <Select skeleton={loading}>
                            <Select.Option value={0}>{t("invoice.create.base.day")}</Select.Option>
                            <Select.Option value={1}>{t("invoice.create.base.week")}</Select.Option>
                            <Select.Option value={2}>{t("invoice.create.base.month")}</Select.Option>
                            <Select.Option value={3}>{t("invoice.create.base.year")}</Select.Option>
                          </Select>
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col xs={6}>
                    <Form.Item label={t("invoice.create.base.invoiceDueDate")} name="dueDateAfterHowManyDay">
                      <Input suffix={t("invoice.create.base.day")} onlyNumbers skeleton={loading} />
                    </Form.Item>
                  </Col>
                  <Col xs={6}>
                    <Form.Item
                      label={t("invoice.create.base.count")}
                      name="count"
                      rules={[
                        {
                          validator: (_rule, value): Promise<void> =>
                            value && Number.isInteger(+value) && +value > 1
                              ? Promise.resolve()
                              : Promise.reject(t("error.scheduleCount")),
                        },
                      ]}
                    >
                      <Input skeleton={loading} onlyNumbers />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item>
                  {repeatDates.length > 0 && (
                    <Row gutter={[8, 8]}>
                      {repeatDates.map((date, index) => (
                        <Col key={index} xs={12} sm={8} md={6} lg={4} xl={4}>
                          <Card.Ant loading={loading}>
                            <Typography.Text>{date.format("jDD jMMMM")}</Typography.Text>
                          </Card.Ant>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Form.Item>
              </>
            )
          );
        }}
      </Form.Item>
      <Form.Item noStyle dependencies={["isSchedule"]}>
        {({ getFieldValue }) => {
          const showDueDate = getFieldValue("isSchedule");
          return (
            !showDueDate && (
              <Form.Item
                label={t("invoice.create.base.dueDate")}
                rules={[{ required: true }]}
                name="dueDate"
                wrapperCol={{ md: 8 }}
                dependencies={["isSchedule"]}
              >
                <DatePicker className="tw-w-full" disabledDate={(date) => isBeforeToday(date)} />
              </Form.Item>
            )
          );
        }}
      </Form.Item>
      <Form.Item>
        <Row gutter={[8, 8]}>
          <Col flex={2}>
            <Button block type={"primary"} htmlType={"submit"}>
              {t("shop.createInvoice.nextStep")}
            </Button>
          </Col>
          <Col flex={1}>
            <Button block onClick={() => prev()}>
              {t("back")}
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

export default SecondStepInvoice;
